import { Box, Fade, Link, Stack } from "@mui/material";
import { ClassExtended, DbUser } from "../../../interfaces";
import Typography from "@mui/material/Typography";
import React from "react";
import { ClassListCard } from "./ClassListCard";
import { UserListCard } from "./UserListCard";
import { useHistory } from "react-router-dom";
import { ClassListSkeleton } from "../utils/ClassListSkeleton";
import { SummaryData } from "@backend-types";
import { HandleDataLayer } from "../../../Helpers";

interface ClassListContainerProps {
  heading: string;
  subHeading?: string | React.ReactNode;
  noDataText: string | React.ReactNode;
  classesExtended: ClassExtended[];
  users?: Partial<DbUser>[];
  variant?: "card" | "grid";
  mainId?: string;
  containerId?: string;
  paginationSelectorComponent?: React.ReactNode;
  showSeeAll?: boolean;
  showSeeAllPath?: string;
  loadingClassData?: boolean;
  classProgressDataMap?: Map<number, SummaryData>;
  studentProgressDataMap?: Map<string, SummaryData>;
}

export const ClassListContainer = (props: ClassListContainerProps) => {
  const {
    classesExtended,
    users,
    heading,
    subHeading,
    noDataText,
    variant = "card",
    mainId,
    containerId,
    paginationSelectorComponent,
    showSeeAll = false,
    showSeeAllPath = "/",
    loadingClassData = false,
    classProgressDataMap,
    studentProgressDataMap,
  } = props;
  const history = useHistory();

  const renderItems = (): React.ReactNode[] => {
    const nodes: React.ReactNode[] = [];

    if (!loadingClassData) {
      nodes.push(
        ...classesExtended.map((classObj, index) => {
          return (
            <Fade in={true} timeout={300} key={`list-class-${heading}-${classObj.id}`}>
              <Box>
                <ClassListCard
                  onClick={(navigateTo: string, classTitle: string) => {
                    HandleDataLayer(
                      "link",
                      "click",
                      "internal link",
                      "main content",
                      heading,
                      classTitle,
                      "onclick"
                    );
                    history.push(navigateTo);
                  }}
                  classExtended={classObj}
                  percentage={
                    classProgressDataMap?.has(classObj.id)
                      ? classProgressDataMap?.get(classObj.id)?.donutPercentage
                      : undefined
                  }
                  variant={variant}
                />
              </Box>
            </Fade>
          );
        })
      );
      users &&
        nodes.push(
          ...users.map((userObj, index) => {
            return (
              <UserListCard
                onClick={(navigateTo: string, userTitle: string) => {
                  HandleDataLayer(
                    "link",
                    "click",
                    "internal link",
                    "main content",
                    heading,
                    userTitle,
                    "onclick"
                  );
                  history.push(navigateTo);
                }}
                key={`list-student-${heading}-${userObj.id}-${userObj.observer_details?.class_id}`}
                user={userObj}
                variant={variant}
                percentage={
                  userObj.id && studentProgressDataMap?.has(userObj.id)
                    ? studentProgressDataMap?.get(userObj.id)?.donutPercentage
                    : undefined
                }
              />
            );
          })
        );
    } else {
      for (let i = 0; i < 6; i++) {
        nodes.push(<ClassListSkeleton key={`classList-skeleton-loader-${i}`} />);
      }
    }
    return nodes;
  };

  const cardItems = renderItems();

  return (
    <>
      <div id=""></div>
      <Box
        id={mainId}
        sx={{
          display: "flex",
          marginTop: "48px",
          marginBottom: "24px",
          alignItems: "center",
          justifyContent: "space-between",
        }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          width="100%">
          <Typography
            sx={{ fontSize: "28px", letterSpacing: "0.28px" }}
            variant="h2"
            component="span">
            {heading}
          </Typography>
          {showSeeAll && cardItems.length > 0 && (
            <Link
              onClick={() => {
                HandleDataLayer(
                  "link",
                  "click",
                  "internal link",
                  "main content",
                  heading,
                  "See all",
                  "onclick"
                );
                history.push(showSeeAllPath);
              }}>
              <Typography
                variant="body1"
                component="span"
                fontSize="1em"
                sx={{ textAlign: "center" }}>
                See all
              </Typography>
            </Link>
          )}
        </Stack>
      </Box>
      {paginationSelectorComponent}
      {subHeading && (
        <Typography
          sx={{ fontSize: "28px", letterSpacing: "0.28px" }}
          variant="h2"
          component="span">
          {subHeading}
        </Typography>
      )}
      <div id={containerId}></div>
      <Stack direction="column" gap={variant === "card" ? "16px" : "0px"}>
        {cardItems.length > 0 ? (
          cardItems.map((node, index) => {
            return node;
          })
        ) : (
          <Box>
            <Typography variant="body2" sx={{}} component="div">
              {noDataText}
            </Typography>
          </Box>
        )}
      </Stack>
    </>
  );
};
